// pages/CompetencyQuiz/QuizResult.js
//
// Round Training-1 (2026-10-26) — one submitted attempt: overall score, band and
// critical misses and the score per domain. IT (Results holders) also see every question with
// the answer given, the correct answer and the rationale; the person who took the quiz does not
// (owner 2026-10-26). The owner sees their own; holders of
// competencyQuiz.results (IT Admin / IT Team) see anyone's (the API decides: 404 otherwise).
//
// Backend: GET /api/competency-quiz/attempts/{id}/result
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Container, Progress, Row, Spinner, Table } from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DashboardPageHeader, EmptyState, StatTile } from "../../Components/Common/DashboardKit";
import useAuth from "../Personal Attendance/useAuth";
import { BAND_TONE, errorText, getResult, pick } from "../../services/competencyQuizApi";
import { BandBadge, fmtDateTime } from "./quizFormat";

const keysText = (q, keys) => (keys.length ? keys.map((k) => `${k}. ${q.options.find((o) => o.key === k)?.text ?? ""}`).join("; ") : "No answer");

export default function QuizResult() {
    document.title = "Training Quiz Result | SSW Technologies";
    const { attemptId } = useParams();
    const navigate = useNavigate();
    const { empId: myEmpId, isItAdmin, canAccessModule } = useAuth();
    const hubOk = isItAdmin || canAccessModule("competencyQuiz.any");
    const back = hubOk ? "/competency-quiz" : "/dashboard";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [show, setShow] = useState("missed");   // missed | all

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await getResult(attemptId);
                if (!cancelled) setData(r);
            } catch (err) {
                if (!cancelled) setError(errorText(err, "Could not load the result."));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [attemptId]);

    const summary = pick(data, "Summary", "summary") || {};
    const domains = pick(data, "Domains", "domains") || [];
    const questions = useMemo(() => (pick(data, "Questions", "questions") || []).map((q) => ({
        order:    pick(q, "DisplayOrder", "displayOrder"),
        code:     pick(q, "QuestionCode", "questionCode"),
        domain:   pick(q, "Domain", "domain"),
        type:     pick(q, "QuestionType", "questionType"),
        text:     pick(q, "QuestionText", "questionText"),
        critical: !!pick(q, "IsCritical", "isCritical"),
        options:  (pick(q, "Options", "options") || []).map((o) => ({ key: pick(o, "Key", "key"), text: pick(o, "Text", "text") })),
        selected: pick(q, "SelectedKeys", "selectedKeys") || [],
        correct:  pick(q, "CorrectKeys", "correctKeys") || [],
        ok:       !!pick(q, "IsCorrect", "isCorrect"),
        rationale: pick(q, "Rationale", "rationale"),
        source:   pick(q, "SourceDocument", "sourceDocument"),
        section:  pick(q, "SourceSection", "sourceSection"),
    })), [data]);

    if (loading) return <div className="page-content"><div className="text-center py-5"><Spinner size="sm" /> Loading…</div></div>;
    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <EmptyState icon="ri-error-warning-line" tone="danger" title="Result unavailable" message={error}
                        action={<Button color="primary" onClick={() => navigate(back)}>{hubOk ? "Back to the quiz" : "Back to the dashboard"}</Button>} />
                </Container>
            </div>
        );
    }

    const name = pick(summary, "EmployeeName", "employeeName");
    const empId = pick(summary, "EmpId", "empId");
    const score = Number(pick(summary, "ScorePercent", "scorePercent") || 0);
    const band = pick(summary, "Band", "band");
    const visible = show === "missed" ? questions.filter((q) => !q.ok) : questions;
    const isMine = String(empId || "").trim() === String(myEmpId || "").trim();
    const canReview = (isItAdmin || canAccessModule("competencyQuiz.results")) && questions.length > 0;

    return (
        <div className="page-content">
            <ToastContainer position="top-right" autoClose={3500} />
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-bar-chart-box-line"
                    eyebrow="Training Quiz · Result"
                    title={name ? `${name} (${empId})` : pick(data, "QuizTitle", "quizTitle")}
                    subtitle={`Submitted ${fmtDateTime(pick(summary, "SubmittedAt", "submittedAt"))}${pick(summary, "AutoSubmitted", "autoSubmitted") ? " · time ran out, submitted automatically" : ""}`}
                    tone="violet"
                    breadcrumb={hubOk ? [{ label: "Training Quiz", to: "/competency-quiz" }, { label: "Result" }] : [{ label: "Dashboard", to: "/dashboard" }, { label: "Training Quiz result" }]}
                    actions={isMine ? <Button color="primary" onClick={() => navigate("/dashboard")}><i className="ri-check-line me-1" />Done</Button> : undefined}
                />

                <Row className="g-3 mb-3">
                    <Col sm={6} xl={3}><StatTile icon="ri-percent-line" label="Overall score" value={score} suffix="%" tone={BAND_TONE[band] || "primary"} /></Col>
                    <Col sm={6} xl={3}><StatTile icon="ri-checkbox-circle-line" label="Correct" value={pick(summary, "CorrectCount", "correctCount") || 0} suffix={` / ${pick(summary, "QuestionCount", "questionCount") || 0}`} tone="success" /></Col>
                    <Col sm={6} xl={3}><StatTile icon="ri-error-warning-line" label="Critical misses" value={pick(summary, "CriticalMisses", "criticalMisses") || 0} tone="danger" /></Col>
                    <Col sm={6} xl={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <CardBody className="d-flex flex-column justify-content-center">
                                <div className="text-muted small mb-1">Assessment band</div>
                                <div className="fs-5"><BandBadge band={band} /></div>
                                <div className="small text-muted mt-1">Internal interpretation, not a pass / fail rule.</div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-3">
                    <CardBody>
                        <h6 className="mb-3">Competency by domain</h6>
                        <div className="table-responsive">
                            <Table className="align-middle mb-0">
                                <thead className="table-light">
                                    <tr><th>Domain</th><th className="text-end">Score</th><th style={{ width: "35%" }} /><th>Band</th></tr>
                                </thead>
                                <tbody>
                                    {domains.map((d) => {
                                        const pct = Number(pick(d, "Percent", "percent") || 0);
                                        const dBand = pick(d, "Band", "band");
                                        return (
                                            <tr key={pick(d, "Domain", "domain")}>
                                                <td>{pick(d, "Domain", "domain")}</td>
                                                <td className="text-end">{pick(d, "Correct", "correct")}/{pick(d, "Total", "total")} ({pct.toFixed(0)}%)</td>
                                                <td><Progress value={pct} color={BAND_TONE[dBand] || "primary"} style={{ height: 8 }} /></td>
                                                <td><BandBadge band={dBand} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>

                {/* IT only (Results holders): the person who took the quiz does not get a question review,
                    and the API does not send them the questions (owner 2026-10-26). */}
                {canReview && (
                    <Card className="border-0 shadow-sm">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                <h6 className="mb-0">Question review</h6>
                                <ButtonGroup size="sm">
                                    <Button color={show === "missed" ? "primary" : "light"} onClick={() => setShow("missed")}>Missed ({questions.filter((q) => !q.ok).length})</Button>
                                    <Button color={show === "all" ? "primary" : "light"} onClick={() => setShow("all")}>All ({questions.length})</Button>
                                </ButtonGroup>
                            </div>
                            {visible.length === 0 ? (
                                <EmptyState compact icon="ri-checkbox-circle-line" tone="success" title="No missed questions" message="Every answer was correct." />
                            ) : visible.map((q) => (
                                <div key={q.code} className={`border-start border-4 ps-3 mb-3 ${q.ok ? "border-success" : "border-danger"}`}>
                                    <div className="d-flex flex-wrap gap-1 mb-1">
                                        <Badge color="light" className="text-dark">{q.order}. {q.code}</Badge>
                                        <Badge color="light" className="text-dark">{q.domain}</Badge>
                                        {q.critical && <Badge color="danger">Critical</Badge>}
                                        <Badge color={q.ok ? "success" : "danger"}>{q.ok ? "Correct" : "Missed"}</Badge>
                                    </div>
                                    <div className="fw-semibold mb-1">{q.text}</div>
                                    <div className={q.ok ? "text-success" : "text-danger"}>Answer given: {keysText(q, q.selected)}</div>
                                    {!q.ok && <div className="text-success">Correct answer: {keysText(q, q.correct)}</div>}
                                    {q.rationale && <div className="text-muted mt-1">{q.rationale}</div>}
                                    {(q.source || q.section) && <div className="small text-muted">Source: {q.source}{q.section ? ` — ${q.section}` : ""}</div>}
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                )}
            </Container>
        </div>
    );
}
